import { memo, useCallback, useMemo, useRef, useState } from 'react';

import {
  type AssetSearchFiltersCalculated,
  type Board,
  type Clip,
  type ClipType,
  SortDirection,
  type SortField,
} from '@air/api/types';
import { Modal, ModalCloseButton, ModalTitle } from '@air/primitive-modal';

import {
  doesNavViewShowBoards,
  doesNavViewShowClips,
  ItemPickerContent,
  type ItemPickerGalleryBoardHandlerProps,
} from '~/components/ItemPicker/ItemPickerContent';
import { ItemPickerFooter } from '~/components/ItemPicker/ItemPickerFooter';
import { type ItemPickerGalleryViewClipsProps } from '~/components/ItemPicker/ItemPickerGalleryView';
import { ItemPickerNavBar } from '~/components/ItemPicker/ItemPickerNavBar';
import { ItemPickerScrollArea } from '~/components/ItemPicker/ItemPickerScrollArea';
import { ItemPickerSidebar } from '~/components/ItemPicker/ItemPickerSidebar';
import { GENERAL_LIBRARY_ID } from '~/components/ItemPicker/itemPickerConstants';
import {
  type ItemPickerItemType,
  type ItemPickerNavView,
  type ItemPickerSelection,
} from '~/components/ItemPicker/itemPickerTypes';
import {
  getItemPickerClipTypes,
  getItemPickerConfirmLabel,
  getItemPickerNavLabel,
} from '~/components/ItemPicker/itemPickerUtils';
import {
  type ItemPickerMarqueeSelection,
  type MarqueeSelectionModifiers,
  useItemPickerMarqueeSelection,
} from '~/components/ItemPicker/useItemPickerMarqueeSelection';
import { SimpleSearchInput } from '~/components/Shared/SimpleSearchInput';
import { ScrollElementContextProvider } from '~/components/v2/components/shared/scroll/ScrollElementContextProvider';
import { useAccountContext } from '~/providers/AccountProvider';

export type { ItemPickerItemType, ItemPickerSelection };

/** Snapshot of both selections at marquee-drag start, used for modifier-held toggle. */
type ItemPickerMarqueeBaseline = {
  boards: Map<Board['id'], Board>;
  clips: Map<Clip['id'], Clip>;
};

/**
 * useRef's initial `null` makes Biome treat `.current` as always null. Widening here keeps the
 * runtime null check as a real branch (modifier-held toggle vs replace-drag).
 */
const readMarqueeBaseline = (value: ItemPickerMarqueeBaseline | null): ItemPickerMarqueeBaseline | null => value;

/** "Recently added" surfaces the newest uploads first — every other view keeps the default name-asc sort. */
const RECENT_SORT_FIELD: SortField = { name: 'dateCreated', direction: SortDirection.desc };

export type ItemPickerProps = {
  /** Which item kinds can be selected. Boards remain browsable for navigation regardless. */
  itemTypes: ItemPickerItemType[];
  /**
   * Optional narrowing of selectable asset clip types (e.g. [ClipType.photo] for a cover picker).
   * Only meaningful when itemTypes includes 'asset'.
   */
  assetTypes?: ClipType[];
  /** Default false: selecting an item replaces the current selection. */
  allowMultiple?: boolean;
  /** Modal title. Default: 'Add from workspace'. */
  title?: string;
  /** Shown in the footer "TO" row; when omitted the row is hidden. */
  destinationLabel?: string;
  /**
   * Overrides the computed confirm-button label entirely (e.g. 'Select image' for a single-select
   * cover/logo picker); when omitted the label is computed as today (Add to X / Add N items).
   * The disabled state is unaffected — confirm still requires a selection.
   */
  confirmLabel?: string;
  onConfirm: (selection: ItemPickerSelection) => void;
};

export const ItemPicker = memo(
  ({
    allowMultiple = false,
    assetTypes,
    confirmLabel,
    destinationLabel,
    itemTypes,
    onClose,
    onConfirm,
    title = 'Add from workspace',
  }: AirModalProps<ItemPickerProps>) => {
    const [navView, setNavView] = useState<ItemPickerNavView>({ kind: 'all' });
    const [history, setHistory] = useState<ItemPickerNavView[]>([]);
    const [future, setFuture] = useState<ItemPickerNavView[]>([]);
    const [search, setSearch] = useState('');
    const [selectedClips, setSelectedClips] = useState<Map<Clip['id'], Clip>>(() => new Map());
    const [selectedBoards, setSelectedBoards] = useState<Map<Board['id'], Board>>(() => new Map());

    const { data: account } = useAccountContext();

    const areBoardsSelectable = itemTypes.includes('board');
    const areClipsSelectable = itemTypes.includes('asset') || itemTypes.includes('file');

    const handleNavViewChange = useCallback(
      (view: ItemPickerNavView) => {
        setHistory((previousHistory) => [...previousHistory, navView]);
        setFuture([]);
        setNavView(view);
        setSearch('');
      },
      [navView],
    );

    const handleBack = useCallback(() => {
      if (history.length === 0) {
        return;
      }

      const previousView = history[history.length - 1];

      setFuture((previousFuture) => [navView, ...previousFuture]);
      setHistory((previousHistory) => previousHistory.slice(0, -1));
      setNavView(previousView);
      setSearch('');
    }, [history, navView]);

    const handleForward = useCallback(() => {
      if (future.length === 0) {
        return;
      }

      const nextView = future[0];

      setHistory((previousHistory) => [...previousHistory, navView]);
      setFuture((previousFuture) => previousFuture.slice(1));
      setNavView(nextView);
      setSearch('');
    }, [future, navView]);

    const selectedBoardIds = useMemo(() => new Set(selectedBoards.keys()), [selectedBoards]);

    const handleToggleClip = useCallback(
      (clip: Clip) => {
        setSelectedClips((previousClips) => {
          const nextClips = new Map(previousClips);

          if (nextClips.has(clip.id)) {
            nextClips.delete(clip.id);
            return nextClips;
          }

          if (allowMultiple) {
            nextClips.set(clip.id, clip);
            return nextClips;
          }

          return new Map([[clip.id, clip]]);
        });

        if (!allowMultiple) {
          setSelectedBoards(new Map());
        }
      },
      [allowMultiple],
    );

    /**
     * One marquee gesture sweeps the whole scroll area, so it manages BOTH selections together.
     * Semantics mirror the workspace views: a plain drag replaces both selections with the
     * marquee'd items; a modifier-held drag toggles the marquee'd items against the selections
     * captured at drag start.
     */
    const marqueeBaselineRef = useRef<ItemPickerMarqueeBaseline | null>(null);

    const handleMarqueeSelectionStart = useCallback(
      (options: MarqueeSelectionModifiers) => {
        // Snapshot at drag start so live marquee updates toggle from a fixed baseline instead of the already-merged selection.
        marqueeBaselineRef.current = options.holdingToggleModifier
          ? { boards: new Map(selectedBoards), clips: new Map(selectedClips) }
          : null;
      },
      [selectedBoards, selectedClips],
    );

    const handleMarqueeSelectionChange = useCallback(
      ({ boards, clips }: ItemPickerMarqueeSelection, options: MarqueeSelectionModifiers) => {
        const baseline = options.holdingToggleModifier ? readMarqueeBaseline(marqueeBaselineRef.current) : null;

        if (baseline) {
          // Symmetric difference against the baseline: each marquee'd item flips its baseline state.
          const nextBoards = new Map(baseline.boards);
          const nextClips = new Map(baseline.clips);

          boards.forEach((board) => {
            if (nextBoards.has(board.id)) {
              nextBoards.delete(board.id);
            } else {
              nextBoards.set(board.id, board);
            }
          });
          clips.forEach((clip) => {
            if (nextClips.has(clip.id)) {
              nextClips.delete(clip.id);
            } else {
              nextClips.set(clip.id, clip);
            }
          });

          setSelectedBoards(nextBoards);
          setSelectedClips(nextClips);
          return;
        }

        // A replace-drag only overwrites the entity types the current view renders: the marquee
        // payload can never contain the other type there (no cards registered), and wiping its map
        // would silently destroy selections made in another view (e.g. rubber-banding boards in a
        // boards-only library dropping the Search view's clip picks).
        if (doesNavViewShowBoards(navView, areBoardsSelectable)) {
          setSelectedBoards(new Map(boards.map((board) => [board.id, board])));
        }

        if (doesNavViewShowClips(navView, areClipsSelectable)) {
          setSelectedClips(new Map(clips.map((clip) => [clip.id, clip])));
        }
      },
      [navView, areBoardsSelectable, areClipsSelectable],
    );

    // Background clicks likewise only clear what the current view shows — selections hidden by the
    // active view survive, consistent with selection persisting across picker navigation.
    const handleMarqueeBackgroundClick = useCallback(() => {
      if (doesNavViewShowBoards(navView, areBoardsSelectable)) {
        setSelectedBoards(new Map());
      }

      if (doesNavViewShowClips(navView, areClipsSelectable)) {
        setSelectedClips(new Map());
      }
    }, [navView, areBoardsSelectable, areClipsSelectable]);

    const marqueeAreaRef = useRef<HTMLDivElement | null>(null);
    const scrollAreaRef = useRef<HTMLDivElement | null>(null);
    const searchAreaRef = useRef<HTMLDivElement | null>(null);

    const { setContainerRef, isMarqueeSelectionEnabled, DragSelection, registerBoardElement, registerClipElement } =
      useItemPickerMarqueeSelection({
        marqueeAreaRef,
        scrollAreaRef,
        searchAreaRef,
        // Single-select pickers keep drag inert — a click is the whole selection gesture there.
        enableMarqueeSelection: allowMultiple,
        areBoardsSelectable,
        areClipsSelectable,
        onMarqueeSelectionStart: handleMarqueeSelectionStart,
        onMarqueeSelectionChange: handleMarqueeSelectionChange,
        onBackgroundClick: handleMarqueeBackgroundClick,
      });

    const handleNavigateToBoard = useCallback(
      (board: Board) => {
        // Prefer the board's own library so Search/favorites/recents drill-in still lists
        // named-library children. Fall back to the current library/board view (including General).
        const inheritedLibraryId =
          navView.kind === 'library' || navView.kind === 'board' ? navView.libraryId : undefined;

        handleNavViewChange({
          kind: 'board',
          boardId: board.id,
          libraryId: board.library?.id ?? inheritedLibraryId,
          label: board.title,
        });
      },
      [handleNavViewChange, navView],
    );

    const handleBoardClick = useCallback(
      (board: Board) => {
        if (!areBoardsSelectable) {
          handleNavigateToBoard(board);
          return;
        }

        setSelectedBoards((previousBoards) => {
          const nextBoards = new Map(previousBoards);

          if (nextBoards.has(board.id)) {
            nextBoards.delete(board.id);
            return nextBoards;
          }

          if (allowMultiple) {
            nextBoards.set(board.id, board);
            return nextBoards;
          }

          return new Map([[board.id, board]]);
        });

        if (!allowMultiple) {
          setSelectedClips(new Map());
        }
      },
      [allowMultiple, areBoardsSelectable, handleNavigateToBoard],
    );

    /**
     * Double-clicking a board browses into it. The double-click's two single-click toggles cancel
     * out, so explicitly removing the board from the selection yields a deterministic
     * "deselected + navigated" end state whether or not the board was selected beforehand. When
     * boards aren't selectable the single click has already navigated, so this is a no-op to avoid
     * navigating twice.
     */
    const handleBoardDoubleClick = useCallback(
      (board: Board) => {
        if (!areBoardsSelectable) {
          return;
        }

        setSelectedBoards((previousBoards) => {
          if (!previousBoards.has(board.id)) {
            return previousBoards;
          }

          const nextBoards = new Map(previousBoards);

          nextBoards.delete(board.id);
          return nextBoards;
        });

        handleNavigateToBoard(board);
      },
      [areBoardsSelectable, handleNavigateToBoard],
    );

    const filters = useMemo<AssetSearchFiltersCalculated>(() => {
      const clipTypes = getItemPickerClipTypes({ itemTypes, assetTypes });
      const typeFilter: AssetSearchFiltersCalculated = clipTypes.length > 0 ? { type: { or: clipTypes } } : {};

      if (navView.kind === 'favorites' && account?.id) {
        return { ...typeFilter, bookmarked: { is: account.id } };
      }

      if (navView.kind === 'library' && navView.libraryId !== GENERAL_LIBRARY_ID) {
        return { ...typeFilter, library: { is: navView.libraryId } };
      }

      if (navView.kind === 'board') {
        return { ...typeFilter, board: { is: navView.boardId } };
      }

      return typeFilter;
    }, [account?.id, assetTypes, itemTypes, navView]);

    const sortField = navView.kind === 'recent' ? RECENT_SORT_FIELD : undefined;

    // Every navView branch renders ONE gallery carrying both the boards and assets sections, so the
    // clips side and the board handlers are shared across the branches below.
    const galleryClipsProps: ItemPickerGalleryViewClipsProps = {
      areClipsSelectable,
      filters,
      onToggleClip: handleToggleClip,
      registerClipElement,
      search,
      selectedClips,
      sortField,
    };

    const galleryBoardHandlerProps: ItemPickerGalleryBoardHandlerProps = {
      areBoardsSelectable,
      onBoardClick: handleBoardClick,
      onBoardDoubleClick: handleBoardDoubleClick,
      registerBoardElement,
      selectedBoardIds,
    };

    const selectedCount = selectedBoards.size + selectedClips.size;

    const handleConfirm = useCallback(() => {
      const boards = [...selectedBoards.values()];
      const clips = [...selectedClips.values()];

      if (boards.length === 0 && clips.length === 0) {
        return;
      }

      onConfirm({ boards, clips });
      onClose();
    }, [onClose, onConfirm, selectedBoards, selectedClips]);

    const footerConfirmLabel = getItemPickerConfirmLabel({
      allowMultiple,
      confirmLabel,
      destinationLabel,
      itemTypes,
      selectedCount,
    });

    return (
      <Modal
        aria-label={title}
        className="mt-4 flex h-[80vh] w-[85vw] max-w-[1000px] flex-col p-0"
        data-testid="ITEM_PICKER"
        isOpen
        onDismiss={onClose}
      >
        <header className="flex shrink-0 items-center justify-between ads:border-default border-gray-4 border-b px-6 py-4">
          <ModalTitle data-testid="ITEM_PICKER_TITLE">{title}</ModalTitle>
          <ModalCloseButton className="shrink-0" onClick={onClose} />
        </header>

        <div className="flex min-h-0 flex-1 overflow-hidden">
          <ItemPickerSidebar navView={navView} onNavViewChange={handleNavViewChange} />

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <ItemPickerNavBar
              canGoBack={history.length > 0}
              canGoForward={future.length > 0}
              label={getItemPickerNavLabel(navView)}
              onBack={handleBack}
              onForward={handleForward}
            />

            {/* Marquee drag starts and background deselect-clicks bind to this wrapper (marqueeAreaRef):
                the search chrome plus the scroll area, so a sweep can begin in the padding around the
                cards. The nav bar stays outside it. */}
            <div className="flex min-h-0 flex-1 flex-col" ref={marqueeAreaRef}>
              <div className="shrink-0 px-6 pt-4 pb-2">
                {/* Hugs the input row exactly (icon + input + clear): presses here keep native
                    search behavior, while the padding around it stays marquee/deselect surface. */}
                <div ref={searchAreaRef}>
                  <SimpleSearchInput
                    data-testid="ITEM_PICKER_SEARCH"
                    onSearchChange={setSearch}
                    placeholder="Search all files and assets"
                    value={search}
                  />
                </div>
              </div>

              <ScrollElementContextProvider>
                <ItemPickerScrollArea scrollAreaRef={scrollAreaRef}>
                  {/* relative positions the marquee selection box by this content column, which spans
                      every section so one drag can sweep boards and assets alike. */}
                  <div className="relative" ref={setContainerRef}>
                    {isMarqueeSelectionEnabled && (
                      // The negative insets stretch the overlay across the scroll area's own padding
                      // (px-6/pt-4/pb-4) so a drag through the gutters shows the box there;
                      // overflow-hidden still stops the box from growing the scroll container's
                      // scrollHeight, which would let edge auto-scroll chase blank space forever.
                      <div className="pointer-events-none absolute -inset-x-6 -top-4 -bottom-4 overflow-hidden">
                        <DragSelection />
                      </div>
                    )}
                    <ItemPickerContent {...galleryClipsProps} {...galleryBoardHandlerProps} navView={navView} />
                  </div>
                </ItemPickerScrollArea>
              </ScrollElementContextProvider>
            </div>
          </div>
        </div>

        <ItemPickerFooter
          confirmLabel={footerConfirmLabel}
          destinationLabel={destinationLabel}
          isConfirmDisabled={selectedCount === 0}
          onCancel={onClose}
          onConfirm={handleConfirm}
        />
      </Modal>
    );
  },
);

ItemPicker.displayName = 'ItemPicker';
